import React, { useCallback, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Collapse, Link, units, Grid } from '@hbf/dsl';
import { useIntl } from 'ha/i18n';
import {
  PropertySizeFilterValue,
  PropertySizeFilterCount,
  PropertySizeFilterStats,
} from 'ha/types/SearchFilters';
import {
  convertSquareMeterToSquareFoot,
  getAreaUnit,
} from 'ha/helpers/unitConverter';

import { CommonRadioFilter } from './CommonRadioFilter';
import { CommonCheckboxTitleWithCount } from './CommonCheckboxTitleWithCount';
import { CommonMinMaxNumericInput } from './CommonMinMaxNumericInput';

interface Props {
  value?: PropertySizeFilterValue;
  onChange: (value: PropertySizeFilterValue) => void;
  count?: PropertySizeFilterCount;
  stats?: PropertySizeFilterStats;
  isImperialSystem: boolean;
}

const useStyles = makeStyles()(() => ({
  link: {
    display: 'inline-block',
    fontSize: units.rem(0.875),
    marginTop: units.rem(0.5),
  },
  inputContainer: {
    paddingBottom: units.rem(0.875),
  },
  clearLinkContainer: {
    marginBottom: units.rem(0.5),
  },
  clearLink: {
    fontSize: units.rem(0.875),
  },
}));

interface InputValue {
  min: string;
  max: string;
}

const PropertyTypeFilter: React.FC<Props> = ({
  value,
  onChange,
  count,
  stats,
  isImperialSystem,
}) => {
  const { classes } = useStyles();
  const { T } = useIntl();

  const [showMinMaxInput, setShowMinMaxInput] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<InputValue>({
    min: '0',
    max: '0',
  });
  const sizeValues = useMemo(
    () => (!isImperialSystem ? [15, 30, 60, 90] : [500, 750, 1000, 1500]),
    [isImperialSystem],
  );
  const areaUnit = useMemo(
    () => getAreaUnit({ isImperialSystem }),
    [isImperialSystem],
  );

  const createOption = useCallback(
    (val: number) => ({
      value: `${val}`,
      title: (
        <CommonCheckboxTitleWithCount
          title={`${val} ${areaUnit} ${T('option.or_more')}`}
          count={count?.[`over${val}`]}
        />
      ),
      testLocator: `Filter ${val} ${areaUnit} or more`,
    }),
    [isImperialSystem, T, count],
  );

  const options = useMemo(
    () => sizeValues.map(val => createOption(val)),
    [sizeValues, createOption],
  );

  const maxStatsSize = useMemo(
    () =>
      !isImperialSystem
        ? stats?.maxValue ?? 0
        : convertSquareMeterToSquareFoot(stats?.maxValue ?? 0),
    [isImperialSystem, stats?.maxValue],
  );

  React.useEffect(() => {
    setInputValue({
      min: String(value?.sizeMin || 0),
      max: String(value?.sizeMax || maxStatsSize),
    });

    if (
      Boolean(value?.sizeMax) ||
      (Boolean(value?.sizeMin) &&
        !options.find(item => item.value === String(value?.sizeMin)))
    ) {
      setShowMinMaxInput(true);
    }
  }, [value, maxStatsSize]);

  const onSelect = React.useCallback(
    minSize => {
      onChange({
        sizeMin: parseInt(minSize, 10),
      });
    },
    [onChange],
  );

  const handleChange = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    setShowMinMaxInput(prev => !prev);
  }, []);

  const onBlur = React.useCallback(() => {
    const inputTmpValue = {
      min: inputValue.min,
      max: inputValue.max,
    };

    if (
      inputValue.min === '' ||
      Number(inputValue.min) < 0 ||
      Number(inputValue.min) > Number(inputValue.max) ||
      Number(inputValue.min) > maxStatsSize
    ) {
      inputTmpValue.min = '0';
      setInputValue(inputTmpValue);
    }

    if (
      inputValue.max === '' ||
      Number(inputValue.max) < 0 ||
      Number(inputValue.max) < Number(inputValue.min) ||
      Number(inputValue.max) > maxStatsSize
    ) {
      inputTmpValue.max = String(maxStatsSize);
      setInputValue(inputTmpValue);
    }

    if (
      !value?.sizeMin &&
      !value?.sizeMax &&
      inputTmpValue.min === '0' &&
      parseInt(inputTmpValue.max, 10) === maxStatsSize
    ) {
      return;
    }

    onChange({
      ...(inputTmpValue.min !== '0' && {
        sizeMin: parseInt(inputTmpValue.min, 10),
      }),
      ...(parseInt(inputTmpValue.max, 10) !== maxStatsSize && {
        sizeMax: parseInt(inputTmpValue.max, 10),
      }),
    });
  }, [onChange, inputValue, maxStatsSize, value]);

  const onMinChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue({
        min: e.target.value,
        max: inputValue.max,
      });
    },
    [inputValue.max],
  );

  const onMaxChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue({
        min: inputValue.min,
        max: e.target.value,
      });
    },
    [inputValue.min],
  );

  const radioValue = React.useMemo(
    () =>
      Boolean(value?.sizeMin) &&
      !value?.sizeMax &&
      options.find(item => item.value === String(value?.sizeMin))
        ? `${value?.sizeMin}`
        : null,
    [value, options],
  );

  return (
    <Grid container direction="column">
      <Grid item>
        <CommonRadioFilter
          name={T('Recently added listings')}
          value={radioValue}
          onChange={onSelect}
          options={options}
        />
      </Grid>
      <Grid item>
        <Link
          to=""
          className={classes.link}
          kind="underline"
          onClick={handleChange}
        >
          {`${showMinMaxInput ? '-' : '+'} ${T(
            'search.filters.property_size.specify.link',
          )}`}
        </Link>
      </Grid>
      <Grid item className={classes.inputContainer}>
        <Collapse in={showMinMaxInput}>
          <CommonMinMaxNumericInput
            id="propertySizeFilter"
            labelMin={T('search.filters.property_size.specify.label_min')}
            valueMin={inputValue.min}
            onMinChange={onMinChange}
            onMinBlur={onBlur}
            labelMax={T('search.filters.property_size.specify.label_max')}
            valueMax={inputValue.max}
            onMaxChange={onMaxChange}
            onMaxBlur={onBlur}
            suffix={areaUnit}
          />
        </Collapse>
      </Grid>
    </Grid>
  );
};

export { PropertyTypeFilter };
